"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUpcomingDealingWindows = exports.hasDealingWindowEnded = exports.parseDealingWindowKey = exports.dealingWindowKeyFor = exports.DEALING_WINDOW_KEY_SEPARATOR = exports.deriveDealingWindows = exports.nextDealingDate = exports.fundLocalDay = exports.hasUsableDealingCycle = exports.formatCutoffTime = exports.UPCOMING_DEALING_DATE_COUNT = void 0;
const date_fns_1 = require("date-fns");
const date_fns_tz_1 = require("date-fns-tz");
const enums_1 = require("./enums");
const IRedemptionConfig_1 = require("./IRedemptionConfig");
/**
 * Dealing-window derivation, shared by the fund settings preview, the investor's lodgement screen and
 * the API. It lives here rather than in either consumer because a window derived one way on screen and
 * another way on the server is how an investor gets told one dealing day and settled on another.
 *
 * Every rule below is expressed in the fund's own end-of-day timezone: the browser may be anywhere, and the
 * API runs UTC, so arithmetic on either one's calendar would move a month-end window by a day.
 */
/** How many upcoming windows the settings screen previews. */
exports.UPCOMING_DEALING_DATE_COUNT = 4;
// Candidate generation is strictly future-dated, so it starts this many days back to keep a window
// dealing *today*. Sized to the 1-business-day close→dealing offset: a Friday end-of-day deals on the
// Monday, so the lookback must span a weekend plus that day.
const CANDIDATE_LOOKBACK_DAYS = 4;
/** `HH:mm` as the 12-hour form the screen shows — `14:00` → `2:00pm`. */
const formatCutoffTime = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const suffix = hours < 12 ? "am" : "pm";
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${String(minutes).padStart(2, "0")}${suffix}`;
};
exports.formatCutoffTime = formatCutoffTime;
/**
 * The zone's own abbreviation at that instant (`AEST` vs `AEDT`), so a preview spanning a DST
 * boundary labels each window with the offset actually in force rather than today's.
 */
const timezoneAbbreviation = (timezone, at) => {
    const parts = new Intl.DateTimeFormat("en-AU", { timeZone: timezone, timeZoneName: "short" }).formatToParts(at);
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
};
/**
 * ISO-8601 weekday (1 = Monday … 7 = Sunday) as date-fns' day index (0 = Sunday). Sunday is the only
 * value that moves, so a plain modulo covers it.
 */
const toDateFnsDay = (isoWeekday) => (isoWeekday % 7);
/**
 * A dealing window closes at the fund's end of day on the business day before its dealing date, so a
 * weekly cycle's dealing day is the business day *after* the configured end-of-day weekday. The other
 * presets take their dealing date from the calendar (month-end / quarter-end) and ignore
 * `cutoff.weekday` entirely. Daily is the exception — see `cutoffDay`.
 */
const candidateDealingDays = (frequency, cutoffWeekday, fundToday, customDealingDates, count) => {
    if (frequency === enums_1.DealingFrequency.CUSTOM) {
        return customDealingDates
            .filter((date) => (0, date_fns_1.isAfter)(date, fundToday))
            .sort((left, right) => left.getTime() - right.getTime())
            .slice(0, count);
    }
    if (frequency === enums_1.DealingFrequency.DAILY) {
        return Array.from({ length: count }, (_value, index) => (0, date_fns_1.addBusinessDays)(fundToday, index + 1));
    }
    if (frequency === enums_1.DealingFrequency.WEEKLY) {
        // From yesterday, so an end-of-day today still counts — the time decides, not the date. Five
        // business days on is the same weekday, hence one closing day per week.
        const firstCutoff = (0, date_fns_1.nextDay)((0, date_fns_1.subDays)(fundToday, 1), toDateFnsDay(cutoffWeekday));
        return Array.from({ length: count }, (_value, index) => (0, date_fns_1.addBusinessDays)(firstCutoff, index * 5 + 1));
    }
    const endOfPeriod = frequency === enums_1.DealingFrequency.MONTHLY ? date_fns_1.endOfMonth : date_fns_1.endOfQuarter;
    const addPeriod = frequency === enums_1.DealingFrequency.MONTHLY ? date_fns_1.addMonths : date_fns_1.addQuarters;
    return Array.from({ length: count }, (_value, index) => (0, date_fns_1.startOfDay)(endOfPeriod(addPeriod(fundToday, index))));
};
/**
 * The day this window opened — the previous cycle's dealing day, except for daily, which opens the day
 * after. Null when the fund has configured no earlier date, which only a custom cycle can do.
 */
const previousDealingDay = (frequency, dealingDate, customDealingDates) => {
    if (frequency === enums_1.DealingFrequency.CUSTOM) {
        const earlier = customDealingDates.filter((date) => date.getTime() < dealingDate.getTime());
        return earlier.length ? earlier.reduce((latest, date) => (date > latest ? date : latest)) : null;
    }
    if (frequency === enums_1.DealingFrequency.DAILY) {
        // The day after, because a daily window closes at its own end of day: the previous one was already
        // shut that evening. Calendar days, so a Monday window opens on the Saturday it started taking
        // requests rather than on a dealing day still ahead of the admin reading it.
        return (0, date_fns_1.addDays)((0, date_fns_1.subBusinessDays)(dealingDate, 1), 1);
    }
    if (frequency === enums_1.DealingFrequency.WEEKLY) {
        return (0, date_fns_1.subBusinessDays)(dealingDate, 5);
    }
    const endOfPeriod = frequency === enums_1.DealingFrequency.MONTHLY ? date_fns_1.endOfMonth : date_fns_1.endOfQuarter;
    const subPeriod = frequency === enums_1.DealingFrequency.MONTHLY ? date_fns_1.subMonths : date_fns_1.subQuarters;
    return (0, date_fns_1.startOfDay)(endOfPeriod(subPeriod(dealingDate, 1)));
};
/** Frequency and end-of-day, once known to be usable. Null when either is missing or unparseable. */
const usableCycle = (config) => {
    const { dealingFrequency, cutoff } = config ?? {};
    if (!dealingFrequency ||
        !IRedemptionConfig_1.REDEMPTION_CUTOFF_TIME_PATTERN.test(cutoff?.time ?? "") ||
        !(0, IRedemptionConfig_1.isSupportedTimezone)(cutoff?.timezone)) {
        return null;
    }
    return {
        frequency: dealingFrequency,
        time: cutoff.time,
        timezone: cutoff.timezone,
        weekday: Number.isInteger(cutoff?.weekday) ? cutoff?.weekday : 1,
    };
};
/** Whether the fund has a frequency and a parseable end of day — not whether any window is upcoming. */
const hasUsableDealingCycle = (config) => usableCycle(config) !== null;
exports.hasUsableDealingCycle = hasUsableDealingCycle;
/**
 * The fund's configured dealing dates as fund-local day starts, ignoring anything the fund did not
 * actually choose. The parse is the rejecting one rather than a plain `new Date`, which maps `null`,
 * `0` and `false` onto the epoch: an unfilled picker row would otherwise become a 1 Jan 1970 dealing
 * date, and while that is too early ever to be a candidate it is still the latest date *before* any
 * real window, so it would be reported as the day that window opened.
 */
const fundLocalCustomDates = (config, timezone) => (config?.customDealingDates ?? [])
    .map(IRedemptionConfig_1.toDealingDate)
    .filter((date) => date !== null)
    .map((date) => (0, date_fns_1.startOfDay)((0, date_fns_tz_1.utcToZonedTime)(date, timezone)));
/**
 * UTC midnight of a fund-local day, not the instant that day begins in the fund's zone. A dealing day is
 * a calendar date, and the platform carries those as UTC midnight (`convertDateToUtcAtMidnight`) so every
 * reader renders the same day. The true instant made Sydney's 31 Aug read as 30 Aug in any browser
 * behind that zone.
 */
const asUtcMidnight = (fundLocalDay) => new Date(`${(0, date_fns_1.format)(fundLocalDay, "yyyy-MM-dd")}T00:00:00.000Z`);
/**
 * The fund-local day a window's end of day falls on: the business day before its dealing day, or the
 * dealing day itself for daily. Taking the day before at a daily cadence closed every window before it
 * opened, so the current window read as "passed" every day of a daily fund's life.
 */
const cutoffDay = (frequency, fundLocalDealingDay) => frequency === enums_1.DealingFrequency.DAILY ? fundLocalDealingDay : (0, date_fns_1.subBusinessDays)(fundLocalDealingDay, 1);
/** The instant a window with this dealing day closes: the fund's end-of-day on its `cutoffDay`. */
const cutoffInstant = (frequency, fundLocalDealingDay, time, timezone) => (0, date_fns_tz_1.zonedTimeToUtc)(`${(0, date_fns_1.format)(cutoffDay(frequency, fundLocalDealingDay), "yyyy-MM-dd")}T${time}:00`, timezone);
/**
 * UTC midnight of the fund's own calendar day at `at`, the way a dealing date is stored. A decision
 * taken out of band deals on the day it is taken, and an instant would render a day early wherever the
 * fund sits ahead of UTC.
 */
const fundLocalDay = (config, at) => {
    const cycle = usableCycle(config);
    return asUtcMidnight((0, date_fns_1.startOfDay)((0, date_fns_tz_1.utcToZonedTime)(at, cycle?.timezone ?? "UTC")));
};
exports.fundLocalDay = fundLocalDay;
/**
 * The window an action taken at `at` lands in: the first window whose dealing day has not passed.
 *
 * End of day deliberately plays no part. A window runs to its dealing day, and a request approved in
 * principle at any point while it is running belongs to it — including on the dealing day itself. Judging
 * this by end of day instead would push a decision taken in the window's last day or two into the
 * following window, splitting a batch the fund had already treated as one.
 *
 * Returns UTC midnight of the fund-local dealing day, and null when the fund's settings do not define a
 * cycle yet.
 */
const nextDealingDate = (config, at) => (0, exports.deriveDealingWindows)(config, at, 1)[0]?.dealingDate ?? null;
exports.nextDealingDate = nextDealingDate;
/**
 * The windows still running at `at`, earliest first — the ones whose dealing day has not passed.
 *
 * The first of these is the current window: what an approval in principle joins, and what the list
 * header names. A window drops out of this set the day after its dealing day, which is exactly when its
 * batch becomes processable — so a processable window is never in here, and the only route to one is the
 * key its own members carry.
 */
const deriveDealingWindows = (config, at, count = 2) => {
    const cycle = usableCycle(config);
    if (!cycle) {
        return [];
    }
    const fundToday = (0, date_fns_1.startOfDay)((0, date_fns_tz_1.utcToZonedTime)(at, cycle.timezone));
    const customDates = fundLocalCustomDates(config, cycle.timezone);
    // Generated from CANDIDATE_LOOKBACK_DAYS back, then filtered forward to today, so a window dealing
    // *today* is kept. The lookback's worth of extra candidates covers the ones the filter discards.
    const candidates = candidateDealingDays(cycle.frequency, cycle.weekday, (0, date_fns_1.subDays)(fundToday, CANDIDATE_LOOKBACK_DAYS), customDates, count + CANDIDATE_LOOKBACK_DAYS)
        .filter((dealingDay) => dealingDay.getTime() >= fundToday.getTime())
        .slice(0, count);
    return candidates.map((dealingDay) => {
        const previous = previousDealingDay(cycle.frequency, dealingDay, customDates);
        const cutoffAt = cutoffInstant(cycle.frequency, dealingDay, cycle.time, cycle.timezone);
        return {
            dealingDayKey: (0, date_fns_1.format)(dealingDay, "yyyy-MM-dd"),
            dealingDate: asUtcMidnight(dealingDay),
            cutoffAt,
            // With no earlier dealing date configured — only a custom cycle can be in that position — the
            // window has been open since whenever the fund set it up, which nothing records. Today is the
            // honest floor rather than a date invented behind it.
            startDate: asUtcMidnight(previous ?? fundToday),
            isClosed: cutoffAt.getTime() <= at.getTime(),
        };
    });
};
exports.deriveDealingWindows = deriveDealingWindows;
exports.DEALING_WINDOW_KEY_SEPARATOR = ":";
/**
 * A class's window key on a dealing day. Day first, so keys sort as strings. Takes the derived day key,
 * not a `Date` — a dealing date is UTC midnight of the fund-local day, a day early in zones behind UTC.
 */
const dealingWindowKeyFor = (dealingDayKey, unitClassId) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dealingDayKey ?? "") || !unitClassId?.trim()) {
        return null;
    }
    return `${dealingDayKey}${exports.DEALING_WINDOW_KEY_SEPARATOR}${unitClassId}`;
};
exports.dealingWindowKeyFor = dealingWindowKeyFor;
/** Splits a window key into its dealing day and unit class, or null if it is not one. */
const parseDealingWindowKey = (windowKey) => {
    const separator = windowKey.indexOf(exports.DEALING_WINDOW_KEY_SEPARATOR);
    if (separator === -1) {
        return null;
    }
    const dayKey = windowKey.slice(0, separator);
    const unitClassId = windowKey.slice(separator + 1);
    if (!unitClassId || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
        return null;
    }
    const [year, month, day] = dayKey.split("-").map(Number);
    // Round-tripped rather than checked for `Invalid Date`, which numeric arguments never produce:
    // `2026-13-45` rolls forward to February 2027 and would name a window the fund never had.
    const dealingDay = new Date(year, month - 1, day);
    if (dealingDay.getMonth() !== month - 1 || dealingDay.getDate() !== day) {
        return null;
    }
    return { dealingDayKey: dayKey, unitClassId };
};
exports.parseDealingWindowKey = parseDealingWindowKey;
/**
 * Whether the window has ended — the gate batch approval sits behind. End of day is not consulted:
 * approving between it and the dealing day would pay a batch still running. Judged in the fund's
 * timezone, from the key's own day, so a window long out of the current set is still answerable.
 *
 * False for an unparseable key or an unusable cycle: refusing is the safe direction for something that
 * authorises payment.
 */
const hasDealingWindowEnded = (config, windowKey, at) => {
    const cycle = usableCycle(config);
    const parsed = (0, exports.parseDealingWindowKey)(windowKey);
    if (!cycle || !parsed) {
        return false;
    }
    return (0, date_fns_1.format)((0, date_fns_1.startOfDay)((0, date_fns_tz_1.utcToZonedTime)(at, cycle.timezone)), "yyyy-MM-dd") > parsed.dealingDayKey;
};
exports.hasDealingWindowEnded = hasDealingWindowEnded;
/**
 * Preview of the next few dealing windows for the settings currently on screen. Purely presentational —
 * it answers "what does this cycle actually mean" for the admin choosing a frequency, and is not the
 * source of truth for any stored window.
 */
const getUpcomingDealingWindows = (config, now) => {
    const cycle = usableCycle(config);
    // The time is pattern-checked, not merely present: a stored value that is not `HH:mm` builds an
    // unparseable instant, and formatting an Invalid Date throws `RangeError` mid-render — a blank page
    // rather than a missing preview.
    if (!cycle) {
        return [];
    }
    // All arithmetic runs on fund-local wall-clock dates so a browser in another zone previews the same
    // days the fund would; each formatted value is converted back to a real instant only for the
    // timezone abbreviation, which needs the true offset.
    const fundToday = (0, date_fns_1.startOfDay)((0, date_fns_tz_1.utcToZonedTime)(now, cycle.timezone));
    // Same lookback-and-filter as `deriveDealingWindows`, so the preview shows the exact windows an
    // approval taken now would join.
    const candidates = candidateDealingDays(cycle.frequency, cycle.weekday, (0, date_fns_1.subDays)(fundToday, CANDIDATE_LOOKBACK_DAYS), fundLocalCustomDates(config, cycle.timezone), exports.UPCOMING_DEALING_DATE_COUNT + CANDIDATE_LOOKBACK_DAYS).filter((dealingDay) => dealingDay.getTime() >= fundToday.getTime());
    return candidates.slice(0, exports.UPCOMING_DEALING_DATE_COUNT).map((dealingDate) => {
        const closesDay = cutoffDay(cycle.frequency, dealingDate);
        const closesAt = cutoffInstant(cycle.frequency, dealingDate, cycle.time, cycle.timezone);
        return {
            dealingDate: (0, date_fns_1.format)(dealingDate, "EEE d MMM yyyy"),
            closes: `${(0, date_fns_1.format)(closesDay, "EEE d MMM")}, ${(0, exports.formatCutoffTime)(cycle.time)} ${timezoneAbbreviation(cycle.timezone, closesAt)}`.trim(),
        };
    });
};
exports.getUpcomingDealingWindows = getUpcomingDealingWindows;
//# sourceMappingURL=dealingWindow.js.map